import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BookOpen,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  AlertCircle,
  Landmark,
  Loader2,
  Sprout,
  Unlock,
  Users,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useParams } from "wouter";
import { WizardProvider, useWizard } from "@/contexts/WizardContext";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { GeneralTab } from "@/components/workflow/GeneralTab";
import { EnvironmentTab } from "@/components/workflow/EnvironmentTab";
import { SocialTab } from "@/components/workflow/SocialTab";
import { GovernanceTab } from "@/components/workflow/GovernanceTab";
import { SustainabilityStoryTab } from "@/components/workflow/SustainabilityStoryTab";
import { getTopicStatus } from "@/components/workflow/common/utils";
import { ReportReadinessModal } from "@/components/workflow/common/ReportReadinessModal";
import { isLastSection, isFirstSection } from "@shared/section-definitions";
import { ChatBubble } from "@/components/chat/ChatBubble";

// Fix Leaflet default icon issue
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { queryClient } from "@/lib/queryClient";
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface WorkflowContentProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

/** Serialize the current card's data slice for the AI chat context. */
function getCardContextData(cardKey: string | undefined, ctx: ReturnType<typeof useWizard>): string | null {
  if (!cardKey) return null;

  const sliceMap: Record<string, () => unknown> = {
    generalInfo: () => ctx.generalInfo,
    sustainability: () => ctx.policyTopics,
    carbonEmissions: () => ({
      fuelEntries: ctx.fuelEntries,
      electricityEntries: ctx.electricityEntries,
      refrigerantEntries: ctx.refrigerantEntries,
      scope3: ctx.scope3Data,
    }),
    pollution: () => ctx.pollutionData,
    biodiversity: () => ctx.biodiversityData,
    water: () => ctx.waterData,
    waste: () => ctx.wasteData,
    riskManagement: () => ctx.riskManagementData,
    employeeSupport: () => ctx.employeeSupportData,
    diversityInclusion: () => ctx.diversityInclusionData,
    humanRights: () => ctx.humanRightsData,
    communityContribution: () => ctx.communityContributionData,
    ethics: () => ctx.ethicsData,
    transparency: () => ctx.transparencyData,
    leadership: () => ctx.leadershipData,
    accountability: () => ctx.accountabilityData,
    storyGeneral: () => ctx.storyGeneralData,
    storyEnvironment: () => ctx.storyEnvironmentData,
    storySocial: () => ctx.storySocialData,
    storyGovernance: () => ctx.storyGovernanceData,
  };

  const getter = sliceMap[cardKey];
  if (!getter) return null;

  const data = getter();
  if (!data) return null;

  try {
    return JSON.stringify(data);
  } catch {
    return null;
  }
}

function WorkflowContent({ activeTab, setActiveTab }: WorkflowContentProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  // Get wizard state from context
  const wizard = useWizard();
  const {
    assessmentId,
    dataLoaded,
    loadingError,
    saveStatus,
    lastSaved,
    forceSave,
    overallCompletion,
    isLoadingGeneral,
    isLoadingEnvironment,
    isLoadingSocial,
    isLoadingGovernance,
    isLocked,
    isReadOnly,
    currentSectionId,
    currentSection,
    goToNextSection,
    goToPreviousSection,
  } = wizard;

  const progress = overallCompletion;
  const canGenerateReport = progress === 100 && isLastSection(activeTab, currentSectionId);
  const canGenerateReportFromHeader = progress === 100;

  // Local UI state
  const [isLocking, setIsLocking] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showReadinessModal, setShowReadinessModal] = useState(false);

  // Fetch user's workspace membership to check role
  const { data: membershipData } = useQuery({
    queryKey: ["workspace-membership"],
    queryFn: async () => {
      const response = await fetch("/api/workspaces/me", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch workspace membership");
      return response.json();
    },
    enabled: !!user,
  });

  const isWorkspaceAdmin = membershipData?.membershipRole === "workspace_admin";

  // Fetch AI quota to determine if chat bubble should be shown
  const { data: aiQuotaData } = useQuery({
    queryKey: ["workspace-ai-quota"],
    queryFn: async () => {
      const response = await fetch("/api/workspace/ai-quota", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch AI quota");
      return response.json();
    },
    enabled: !!user,
  });

  const hasAiBudget = aiQuotaData?.quota != null && aiQuotaData.usedCents < aiQuotaData.quota.budgetCents;

  const handleSave = async () => {
    await forceSave();
    toast({
      title: "Draft Saved",
      description: "Your changes have been saved as a draft.",
    });
  };

  // Open the readiness modal when user wants to generate report
  const handleGenerateReport = () => {
    setShowReadinessModal(true);
  };

  // Called from the modal when user confirms report generation
  const handleProceedWithReport = async (narrative: string | null) => {
    if (!assessmentId) {
      toast({
        title: "Error",
        description: "No assessment found.",
        variant: "destructive",
      });
      return;
    }

    setIsLocking(true);
    try {
      // Step 1: persist narrative
      const patchResponse = await fetch(`/api/assessments/${assessmentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportNarrative: narrative }),
      });

      if (!patchResponse.ok) {
        const errorData = await patchResponse.json().catch(() => ({ error: "Failed to save narrative." }));
        throw new Error(errorData.error || "Failed to save narrative.");
      }

      // Step 2: lock the assessment
      const lockResponse = await fetch(`/api/assessments/${assessmentId}/lock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!lockResponse.ok) {
        const errorData = await lockResponse.json().catch(() => ({ error: "Failed to lock assessment." }));
        throw new Error(errorData.error || "Failed to lock assessment.");
      }

      // Invalidate report cache when assessment is locked
      queryClient.invalidateQueries({ queryKey: ["report-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["current-assessment"] });

      toast({
        title: "Baseline complete",
        description: "Assessment locked and report is being generated.",
      });
      setShowReadinessModal(false);
      setLocation("/report");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setIsLocking(false);
    }
  };

  const handleUnlock = async () => {
    if (!assessmentId) {
      toast({
        title: "Error",
        description: "No Assessment Found",
        variant: "destructive",
      });
      return;
    }

    setIsUnlocking(true);
    try {
      const response = await fetch(`/api/assessments/${assessmentId}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unlock Failed" }));
        throw new Error(errorData.error || "Unlock Failed");
      }

      // Invalidate report cache when assessment is unlocked
      queryClient.invalidateQueries({ queryKey: ["report-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["current-assessment"] });

      toast({
        title: "Assessment Unlocked",
        description: "You can now edit the assessment again.",
      });

      // Refresh the page to reload assessment data
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Unlock Failed",
        variant: "destructive",
      });
    } finally {
      setIsUnlocking(false);
    }
  };

  // Show loading state while initializing
  if (!dataLoaded && !loadingError) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card className="border-2">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-2">{"Loading"}</h3>
            <p className="text-muted-foreground">{"Please wait while we load your workflow..."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state and redirect to assessments list if assessment not found
  if (loadingError || !assessmentId) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="border-2 shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-primary/10 p-4">
                <AlertCircle className="h-12 w-12 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-2xl">{"Not Found"}</CardTitle>
            <CardDescription className="text-base mt-2">
              {loadingError ? (
                <span className="text-destructive">{loadingError}</span>
              ) : (
                "The requested workflow or assessment could not be found."
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <Button
                className="bg-[#00444C] hover:bg-[#001F24]"
                onClick={() => setLocation("/assessments")}
              >
                {"Go to List"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2 text-amber-800 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{"This assessment is read-only."}</span>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">{"Workflow"}</h1>
          <p className="text-muted-foreground">{"Manage your assessment workflow"}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Auto-save indicator */}
          {!isReadOnly && (
            <>
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card px-3 py-2 rounded-lg border">
                {saveStatus === "saving" ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{"Saving..."}</span>
                  </>
                ) : saveStatus === "saved" ? (
                  <>
                    <Check className="h-3 w-3 text-green-600" />
                    <span className="text-green-600">{"Saved"}</span>
                  </>
                ) : lastSaved ? (
                  <>
                    <Check className="h-3 w-3 text-muted-foreground" />
                    <span>{"Auto-saved"}</span>
                  </>
                ) : (
                  <span>{"Auto-save enabled"}</span>
                )}
              </div>
              {isLocked && isWorkspaceAdmin ? (
                <Button
                  size="sm"
                  onClick={handleUnlock}
                  disabled={isUnlocking}
                  variant="outline"
                  className="h-7 border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  {isUnlocking ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      {"Unlocking..."}
                    </>
                  ) : (
                    <>
                      <Unlock className="mr-1 h-3 w-3" />
                      {"Unlock"}
                    </>
                  )}
                </Button>
              ) : !isLocked && canGenerateReportFromHeader ? (
                <Button
                  size="sm"
                  onClick={handleGenerateReport}
                  disabled={isLocking}
                  className="h-7"
                  style={{ backgroundColor: "#00444C" }}
                >
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  {"Generate Report"}
                </Button>
              ) : null}
            </>
          )}
          <div className="flex items-center gap-4 bg-card p-3 rounded-xl border shadow-sm">
            <div className="text-sm font-medium">{"Completion"}</div>
            <Progress value={progress} className="w-32 md:w-48 h-2 bg-muted" />
            <span className="text-sm font-bold" style={{ color: '#00444C' }}>{progress}%</span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="sticky top-0 z-40 bg-white">
        <TabsList className="grid h-[56px] w-full grid-cols-5 items-end rounded-[10px] border-b border-slate-500/10 bg-white px-[18px]">
          <TabsTrigger
            value="general"
            icon={BookOpen}
            iconClassName="text-primary"
            className="h-full justify-center px-3 text-[15px] leading-6"
          >
            General
          </TabsTrigger>
          <TabsTrigger
            value="environment"
            icon={Sprout}
            iconClassName="text-primary"
            className="h-full justify-center px-3 text-[15px] leading-6"
          >
            Environment
          </TabsTrigger>
          <TabsTrigger
            value="social"
            icon={Users}
            iconClassName="text-primary"
            className="h-full justify-center px-3 text-[15px] leading-6"
          >
            Social
          </TabsTrigger>
          <TabsTrigger
            value="governance"
            icon={Landmark}
            iconClassName="text-primary"
            className="h-full justify-center px-3 text-[15px] leading-6"
          >
            Governance
          </TabsTrigger>
          <TabsTrigger
            value="sustainability-story"
            icon={FileText}
            iconClassName="text-primary"
            className="h-full justify-center px-3 text-[15px] leading-6"
          >
            Sustainability Story
          </TabsTrigger>
        </TabsList>
        </div>

        {/* GENERAL TAB */}
        <TabsContent value="general" className="space-y-6">
          {isLoadingGeneral ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{"Loading General section..."}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <GeneralTab />
              <div className="flex justify-between">
                {!isFirstSection(activeTab, currentSectionId) && (
                  <Button variant="outline" onClick={() => goToPreviousSection()} className="gap-2">
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </Button>
                )}
                <Button onClick={() => goToNextSection()} className="gap-2 ml-auto" style={{ backgroundColor: '#00444C' }}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* ENVIRONMENT TAB */}
        <TabsContent value="environment" className="space-y-6">
          {isLoadingEnvironment ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{"Loading Environment section..."}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <EnvironmentTab />
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => goToPreviousSection()} className="gap-2">
                  <ChevronLeft className="h-4 w-4" /> {"Previous"}
                </Button>
                <Button onClick={() => goToNextSection()} className="gap-2" style={{ backgroundColor: '#00444C' }}>
                  {"Next"} <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* SOCIAL TAB */}
        <TabsContent value="social" className="space-y-6">
          {isLoadingSocial ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{"Loading Social section..."}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <SocialTab />
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => goToPreviousSection()} className="gap-2">
                  <ChevronLeft className="h-4 w-4" /> {"Previous"}
                </Button>
                <Button onClick={() => goToNextSection()} className="gap-2" style={{ backgroundColor: '#00444C' }}>
                  {"Next"} <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* GOVERNANCE TAB */}
        <TabsContent value="governance" className="space-y-6">
          {isLoadingGovernance ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{"Loading Governance section..."}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <GovernanceTab />
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => goToPreviousSection()} className="gap-2">
                  <ChevronLeft className="h-4 w-4" /> {"Previous"}
                </Button>
                <Button onClick={() => goToNextSection()} className="gap-2" style={{ backgroundColor: '#00444C' }}>
                  {"Next"} <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* SUSTAINABILITY STORY TAB */}
        <TabsContent value="sustainability-story" className="space-y-6">
          <SustainabilityStoryTab />
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => goToPreviousSection()} className="gap-2">
              <ChevronLeft className="h-4 w-4" /> {"Previous"}
            </Button>
            <div className="flex gap-2">
              {/* Show Generate Report only on the last section of the last tab */}
              {isReadOnly ? null : isLocked && isWorkspaceAdmin && isLastSection(activeTab, currentSectionId) ? (
                <Button
                  size="lg"
                  onClick={handleUnlock}
                  disabled={isUnlocking}
                  variant="outline"
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  {isUnlocking ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {"Unlocking..."}
                    </>
                  ) : (
                    <>
                      <Unlock className="mr-2 h-4 w-4" />
                      {"Unlock Assessment"}
                    </>
                  )}
                </Button>
              ) : !isLocked && canGenerateReport ? (
                <Button
                  size="lg"
                  onClick={handleGenerateReport}
                  disabled={isLocking}
                  style={{ backgroundColor: '#00444C' }}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {"Generate Pilot Report"}
                </Button>
              ) : !isLocked || !isLastSection(activeTab, currentSectionId) ? (
                <Button onClick={() => goToNextSection()} className="gap-2" style={{ backgroundColor: '#00444C' }}>
                  {"Next"} <ChevronRight className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
          {isLocked && !isWorkspaceAdmin && (
            <p className="text-sm text-muted-foreground text-center mt-2">
              {"This assessment is currently locked by another user or process."}
            </p>
          )}
        </TabsContent>
      </Tabs>

      {/* Report Readiness Modal */}
      {assessmentId && (
        <ReportReadinessModal
          open={showReadinessModal}
          onOpenChange={setShowReadinessModal}
          assessmentId={assessmentId}
          onProceed={handleProceedWithReport}
          isProceedLoading={isLocking}
        />
      )}

      {assessmentId && !isLocked && user?.role !== "admin" && hasAiBudget && (
        <ChatBubble
          assessmentId={assessmentId}
          cardContextLabel={currentSection?.displayName}
          cardContextData={getCardContextData(currentSection?.cardKey, wizard) ?? undefined}
        />
      )}
    </div>
  );
}

export default function Workflow() {
  const params = useParams<{ assessmentId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Validate assessment ID from URL
  const assessmentId = params.assessmentId;

  if (!assessmentId) {
    // Redirect to assessments list if no ID provided
    setLocation("/assessments");
    return null;
  }

  const validTabs = ["general", "environment", "social", "governance", "sustainability-story"];
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get("tab");
  const sectionParam = urlParams.get("section");
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : "general";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("tab", tab);
    // Clear section param when tab changes (will be set by context)
    newParams.delete("section");
    window.history.replaceState({}, "", `${window.location.pathname}?${newParams.toString()}`);
  };

  // Update URL when section changes
  const handleSectionChange = (sectionId: string) => {
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("section", sectionId);
    window.history.replaceState({}, "", `${window.location.pathname}?${newParams.toString()}`);
  };

  return (
    <WizardProvider
      activeTab={activeTab}
      assessmentId={assessmentId}
      onTabChange={handleTabChange}
      onSectionChange={handleSectionChange}
      initialSectionId={sectionParam || undefined}
    >
      <WorkflowContent activeTab={activeTab} setActiveTab={handleTabChange} />
    </WizardProvider>
  );
}
